import { useQuery } from "@tanstack/react-query";
import { apiGet, apiPost } from "./client";

/** Which Snapshot sheet an embed refers to. Matches the backend's SHEETS keys. */
export type SupersetSheetKey = "sellFactor" | "requiredYield";

export interface SupersetSheet {
  key: SupersetSheetKey;
  /** Label for the toggle, supplied by the backend so the two stay in step. */
  label: string;
  /** Superset dashboard slug — diagnostics only; the SDK mounts by uuid. */
  dashboard: string;
  /** Embedded-registration id the Superset SDK mounts. Null until the sheet's
   *  SUPERSET_DASHBOARD_UUID_* is pinned in the backend's env. */
  uuid: string | null;
}

export interface SupersetSheets {
  /** Origin the SDK loads Superset from. */
  supersetDomain: string;
  defaultSheet: SupersetSheetKey;
  sheets: SupersetSheet[];
}

export interface SupersetGuestToken {
  token: string;
  uuid: string;
  view: SupersetSheetKey;
  supersetDomain: string;
  /** The RLS clauses the backend actually put in the token. */
  rls?: string[];
  /** Filter values the backend refused to translate. */
  warnings?: string[];
}

export interface SupersetOffice {
  officeNumber: number;
  /** '#5-Dealer Services' — the same label the sheet's rows are grouped by. */
  officeLabel: string;
}

export interface SupersetFilterOptions {
  /** Newest first; the page defaults to [0]. */
  dates: string[];
  industries: string[];
  offices: SupersetOffice[];
}

/**
 * What the page's filter bar narrows the embedded sheet to.
 *
 * Sent as plain values, NOT as SQL: rbp-api maps them through allowlists into the
 * guest token's RLS clauses. That is the only mechanism that actually filters an
 * embedded dashboard on this deployment — Superset does not bind arbitrary query
 * params to native filters, and template processing is off — which is also why
 * the dashboards carry no native filters of their own.
 *
 * Omitting `asOf` does not mean "all dates": the backend pins the newest snapshot
 * instead, because a sheet with no date clause would silently aggregate all twelve
 * monthly snapshots into each cell.
 */
export interface SupersetFilters {
  /** YYYY-MM-DD. Resolves server-side to the newest snapshot at or before this day. */
  asOf?: string;
  /** A SIC division exactly as it appears on the sheet. Empty = all industries. */
  borrowerIndustry?: string;
  /** office_number, not the label. Empty = all offices. */
  office?: number | "";
}

/** Option lists for the three filters, read from the sheet view itself — so every
 *  option offered is guaranteed to render a non-empty sheet. The portfolio-wide
 *  /offices list would offer two offices the sheet has no rows for. */
export function useSupersetFilterOptions() {
  return useQuery({
    queryKey: ["rbpe-superset-filter-options"],
    queryFn: () => apiGet<SupersetFilterOptions>("/superset/filter-options"),
  });
}

/** The Snapshot page's two embeddable sheets: Sell Factor and Required Yield.
 *  Each is its own Superset dashboard, so switching the toggle swaps the embed
 *  rather than filtering one dashboard. */
export function useSupersetSheets() {
  return useQuery({
    queryKey: ["rbpe-superset-sheets"],
    queryFn: () => apiGet<SupersetSheets>("/superset/dashboards"),
  });
}

/** Mints a guest token for one sheet, carrying the page's filters as RLS.
 *
 *  Not a hook: the Superset SDK calls this itself, both on mount and again
 *  whenever the token nears expiry (guest tokens are short-lived by design), so
 *  it has to be an ordinary awaitable rather than tied to React's render cycle.
 *
 *  Takes the sheet and the filters as arguments rather than reading them from
 *  state for the same reason — the SDK holds onto this function and calls it long
 *  after the component that made it re-rendered, so both have to be bound into
 *  the closure, or a token refresh would re-mint for the wrong sheet or the wrong
 *  filters. */
export async function fetchSupersetGuestToken(
  view: SupersetSheetKey,
  filters?: SupersetFilters,
): Promise<string> {
  const res = await apiPost<SupersetGuestToken>("/superset/guest-token", {
    username: "athena-user",
    view,
    ...(filters ? { filters } : {}),
  });
  if (res.warnings?.length) {
    // Not thrown: the sheet renders correctly, just less narrowed than asked.
    // Silence here is what makes a dead filter hard to notice.
    console.warn("[rbpe] Superset filter not applied:", res.warnings.join(" "));
  }
  return res.token;
}
